/* eslint-disable react/prefer-stateless-function */
import React from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';
import { camelizeKeys } from '../reverb-js-common/hash_util';
import {
  core_apimessages_GuestCheckoutApplePayLineItem as LineItem,
  GuestCheckoutApplePayLineItems,
  GuestCheckoutApplePayPayment,
  core_apimessages_GuestCheckoutApplePayPaymentRequest as GuestCheckoutApplePayPaymentRequest,
  core_apimessages_GuestCheckoutApplePayLineItemsRequest as GuestCheckoutApplePayLineItemsRequest,
  core_apimessages_GuestCheckoutApplePayPaymentResponse as GuestCheckoutApplePayPaymentResponse,
  core_apimessages_CartItemAttributes as CartItemAttributes,
  core_apimessages_Address as Address,
  core_apimessages_Money as Money,
} from '../schemas/rql';
import gql from 'graphql-tag';
import bind from '@reverbdotcom/commons/src/bind';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import Location from '../lib/wrapped_location';
import * as elog from '@reverbdotcom/commons/src/elog';
import ApplePay from '@adyen/adyen-web/dist/types/components/ApplePay';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import I18n from 'i18n-js';
import { getApplePayConfiguration, paymentContactToAddressParams } from '../shared/apple_pay';

const APPLE_PAY_PAYMENT_METHOD = 'apple_pay';

interface ApplePayProps {
  listingCurrencyCode: string;
  paymentMethodsConfig: string;
  payButtonType?: 'checkout' | 'plain';
  cartItemAttributes?: CartItemAttributes[];
  buttonLocation?: 'hoisted' | 'default';
}
interface ApolloProps {
  guestCheckoutApplePayLineItems: MutationFn<GuestCheckoutApplePayLineItems.Mutation, GuestCheckoutApplePayLineItems.Variables>;
  guestCheckoutApplePayPayment: MutationFn<GuestCheckoutApplePayPayment.Mutation, GuestCheckoutApplePayPayment.Variables>;
}

interface State {
  applePay?: ApplePay,
  totalAmount?: Money,
  paymentResult?: GuestCheckoutApplePayPaymentResponse,
  error: string,
  showModal: boolean,
}

type Props = ApplePayProps & ApolloProps & IUserContext;

export class LoggedOutApplePayButton extends React.Component<Props, State> {
  state = {
    applePay: undefined,
    totalAmount: undefined,
    paymentResult: undefined,
    error: undefined,
    showModal: false,
  };

  openPaymentSheet(e) {
    e.preventDefault();
    this.state.applePay.submit();
  }

  @bind
  async onAuthorized(resolve, _reject, event) {
    const checkoutId = `RCP-${this.props.listingCurrencyCode}-apple_pay`;
    const { payment } = event;
    const input: GuestCheckoutApplePayPaymentRequest = {
      totalPrice: {
        amountCents: this.state.totalAmount.amountCents,
        currency: this.state.totalAmount.currency,
      },
      checkoutId,
      cartItemAttributes: this.props.cartItemAttributes,
      adyenToken: btoa(JSON.stringify(payment.token.paymentData)),
      shippingAddress: paymentContactToAddressParams(payment.shippingContact),
      firstName: payment.shippingContact.givenName,
      lastName: payment.shippingContact.familyName,
      email: payment.shippingContact.emailAddress,
    };

    try {
      const result = await this.props.guestCheckoutApplePayPayment({
        variables: {
          input,
        },
      });

      const paymentResult = result ? camelizeKeys(result.data.guestCheckoutApplePayPayment) : {};
      if (paymentResult === {}) {
        const paymentError = new Error('checkout.apple_pay.make_payment_error');
        elog.error('checkout.apple_pay.make_payment_error', {}, paymentError);
      }
      resolve();
      this.setState({ paymentResult });
    } catch (e) {
      elog.error('checkout.apple_pay.make_payment_error', {}, e);
      // error corresponds to: https://github.com/reverbdotcom/reverb/blob/master/config/locales/controllers/bundled_checkout/en.yml#L18
      if (e.message.includes('Please log in to complete this order')) {
        this.setState({ error: I18n.t('You already have an account on Reverb. Please log in to complete this order.') });
      }
    }
  }

  @bind
  async onShippingContactSelected(resolve, _reject, event) {
    const { shippingContact } = event;
    const shippingAddress: Address = {
      streetAddress: shippingContact.addressLines && shippingContact.addressLines[0],
      extendedAddress: shippingContact.addressLines && shippingContact.addressLines[1],
      locality: shippingContact.locality,
      postalCode: shippingContact.postalCode,
      region: shippingContact.administrativeArea,
      countryCode: shippingContact.countryCode?.toUpperCase(),
    };
    const checkoutId = `RCP-${this.props.listingCurrencyCode}-apple_pay`;
    const input: GuestCheckoutApplePayLineItemsRequest = {
      shippingAddress,
      checkoutId,
      paymentMethod: APPLE_PAY_PAYMENT_METHOD,
      cartItemAttributes: this.props.cartItemAttributes,
    };
    const result = await this.props.guestCheckoutApplePayLineItems({
      variables: {
        input,
      },
    });
    if (!result) {
      const e = new Error('checkout.apple_pay.fetch_line_items_error');
      elog.error(e.message, {}, e);
      return;

    }
    const lineItems = result.data?.guestCheckoutApplePayLineItems?.lineItems;
    const total = result.data?.guestCheckoutApplePayLineItems?.total;
    if (lineItems == null) {
      const e = new Error('checkout.apple_pay.fetch_line_items_error');
      elog.error(e.message, {}, e);
      return;
    }
    const totalA: Money = total.amount;
    this.setState({ totalAmount: totalA });

    const getLineItem = (lineItem: LineItem): ApplePayJS.ApplePayLineItem => {
      return {
        amount: lineItem.amount.amount,
        label: lineItem.description,
        type: 'final',
      };
    };
    resolve({ newTotal: getLineItem(total), newLineItems: lineItems.map((lineItem) => getLineItem(lineItem)) });
  }

  closeModal() {
    this.setState({ error: undefined, showModal: false });
  }

  async componentDidMount() {
    const onError = () => this.setState({ showModal: true });
    const applePay = getApplePayConfiguration(this.props.user, this.props.paymentMethodsConfig, this.props.listingCurrencyCode, this.onShippingContactSelected, this.onAuthorized, onError);
    this.setState({ applePay });
  }

  render() {
    if (this.state.paymentResult?.orderBundleId) {
      Location.assign(this.state.paymentResult.paymentResultUrl);
    }

    const classes = classNames(
      'apple-pay-button',
      { 'apple-pay-button--checkout': this.props.payButtonType === 'checkout' },
    );

    return (
      <>
        <button
          type="button"
          onClick={(e) => buildTrackAndOpenPaymentSheet({
            openPaymentSheet: this.openPaymentSheet(e),
            buttonLocation: this.props.buttonLocation,
          })}
          className={classes}
          aria-label="Check out with Apple Pay"
        />
        <SignupSigninModal
          messageComponent={
            <div className="mb-2">
              <RCAlertBox type="warning" small>
                {this.state.error}
              </RCAlertBox>
            </div>
          }
          active={this.state.error && this.state.showModal}
          onRequestClose={() => this.closeModal()}
          showSignInOnOpen
        />
      </>
    );
  }
}

function buildTrackAndOpenPaymentSheet({ openPaymentSheet, buttonLocation }) {
  return function trackAndOpenPaymentSheet(event) {
    trackEvent({
      eventName: MParticleEventName.ClickedApplePayButton,
      displayStyle: buttonLocation === 'hoisted' ? 'hoisted' : 'default',
    });
    openPaymentSheet(event);
  };
}

const guestCheckoutApplePayLineItems =
  withGraphql<ApplePayProps, GuestCheckoutApplePayLineItems.Mutation, GuestCheckoutApplePayLineItems.Variables>(
    gql`
    mutation GuestCheckoutApplePayLineItems($input: Input_core_apimessages_GuestCheckoutApplePayLineItemsRequest) {
      guestCheckoutApplePayLineItems(input: $input) {
        lineItems {
          name
          description
          amount {
            amount
            amountCents
            currency
            symbol
            display
          }
        }
        total {
          name
          description
          amount {
            amount
            amountCents
            currency
            symbol
            display
          }
        }
      }
    }`,
    {
      name: 'guestCheckoutApplePayLineItems',
    },
  );

const guestCheckoutApplePayPayment =
  withGraphql<ApplePayProps, GuestCheckoutApplePayPayment.Mutation, GuestCheckoutApplePayPayment.Variables>(
    gql`
    mutation GuestCheckoutApplePayPayment($input: Input_core_apimessages_GuestCheckoutApplePayPaymentRequest) {
      guestCheckoutApplePayPayment(input: $input) {
        orderBundleId
        paymentResultUrl
      }
    }`,
    {
      name: 'guestCheckoutApplePayPayment',
    },
  );

export default flowRight([guestCheckoutApplePayLineItems, guestCheckoutApplePayPayment])(withUserContext(LoggedOutApplePayButton));
